/**
 * F 144 — remote state for FCC.
 *
 * WHY A WRITE-BEHIND CACHE AND NOT AN ASYNC ADAPTER
 * ------------------------------------------------
 * The workspace storage layer is SYNCHRONOUS: `loadPages()` returns a value,
 * it does not return a Promise. Any server-backed store is inherently async,
 * so pointing the `StorageAdapter` interface at a server would force every
 * caller to become async and ripple through every component that reads state
 * during render. That is a rewrite, not a migration.
 *
 * So the sync contract is PRESERVED: state is hydrated into memory once at
 * boot, sync reads come from memory, and writes update memory immediately
 * (so the UI is never stale) and are flushed to the server on a debounce.
 * A reload re-hydrates from the server, which is what makes state
 * cross-device — and what finally makes automated verification possible.
 *
 * This is deliberately NOT a general StorageAdapter. It covers the FCC
 * workspace keys, which are a plain KV namespace (`freshcards:*`). UI
 * preferences (theme, density, skin) deliberately stay in localStorage:
 * they are per-device by nature and there is no reason to sync them.
 *
 * DEGRADATION
 * -----------
 * If the endpoint is unreachable — a static deploy, a dev machine, a
 * network blip — every call falls back to localStorage. The app keeps
 * working exactly as it did before; it simply does not sync. A storage
 * migration must never be able to brick the app it is migrating.
 */

import type { StorageAdapter } from './types';

/** Keys that are DATA and must cross devices. */
const SYNC_PREFIX = 'freshcards:';
/** Legacy data keys that predate the `freshcards:` namespace. */
const LEGACY_SYNC_KEYS = ['freshcards_database_records', 'appState'];

/** Keys that stay on this device only. Never sent. */
const DEVICE_ONLY = new Set<string>([
  'freshcards-theme',
  'freshcards-skin',
  'freshcards:layout',
  'mantine-color-scheme-value',
  'freshcards:listDensity',
  'freshcards:galleryDensity',
]);

/**
 * F 451 — THE MIRROR IS BOUNDED BY localStorage, AND hq_item:* DOES NOT FIT.
 *
 * `init()` reads `GET /kv?all=1`, which returns EVERY syncable key and value,
 * and this module mirrors each one into `localStorage` so a cold start and a
 * warm start read the same store. That mirror is fine while the dataset is
 * small. It stops being fine at scale, and the way it failed was total:
 *
 *   `freshcards:hq_item:` is 16,784 keys / 20,454,802 bytes (measured on
 *   production 2026-10-22). localStorage's quota is ~5 MB. So hydration threw
 *   part-way through the loop, `main.tsx` caught it, logged one `console.warn`
 *   (`[fcc] remote state unavailable, using local:`), and the app spent the
 *   rest of the session in local-storage-only mode — the whole app, silently,
 *   because of ONE namespace nobody was reading through this module.
 *
 * WHY EXCLUDE IT HERE AND NOT WHERE IT IS WRITTEN. Nothing reads hq_item keys
 * through `RemoteState`. `core/hq/hqBackup.ts` reads the prefix directly with
 * `graphFetch` and parses the envelope itself, deliberately: its summary read
 * is 1 key and its item read is ~11.5 MB and happens ONLY when the user opens
 * the database. Measured: `hq_item` appears in exactly one non-test source
 * file. So excluding the prefix from the MIRROR costs that reader nothing —
 * it never went through here — while removing 20 MB from a 5 MB budget.
 *
 * `freshcards:hq_backup_manifest` (1 key) is deliberately NOT excluded: the
 * sidebar reads it through `graphFetch` too, but it is the cheap summary that
 * makes the HQ Backup row appear at all, and it costs ~2 KB.
 */
const MIRROR_EXCLUDED_PREFIXES: readonly string[] = ['freshcards:hq_item:'];

/**
 * F 452 — THE NAMESPACES THE BOOT DOES NOT DOWNLOAD.
 *
 * The MIRROR exclusion above is about the browser's DURABLE copy. This one is
 * about the DOWNLOAD, and the two are not the same question: a namespace can
 * be worth keeping in memory (it is in the envelope either way) and still be
 * too big to fetch on every boot.
 *
 * MEASURED on production, 2026-10-28, through the service's own route:
 *
 *   the graph, whole                 722 syncable keys / 18.1 MB of values
 *   of which the item chunks           49 keys / 16.9 MB   (93% of the bytes)
 *   the rest                          673 keys /   169 KB
 *
 * The 49 chunk keys are `freshcards:db:<id>:items:p0000…p0048` — a database's
 * items, stored as 49 JSON arrays of ~342 items each. The boot read all of
 * them (`GET /kv?all=1`) and NOTHING in the boot path reads them: the
 * database surface asks the graph for ONE PAGE at a time through
 * `graphFetch` (F 486's route, F 497's reader), and `core/hq/hqBackup.ts`
 * reads its own prefix directly. Measured: `:items:` appears in no
 * non-test source file, and `freshcards:db:` appears in none either.
 *
 * ⇒ THE BOOT ASKS FOR EVERYTHING EXCEPT THOSE NAMESPACES, and the service
 *   applies the exclusion AT THE STORE, so the rows are never read, never
 *   parsed and never counted. Measured on the same day:
 *
 *     before   18,454,013 bytes / 1.03 s
 *     after       604,624 bytes / 0.06 s
 *
 * `freshcards:hq_item:` is excluded for the same reason and with F 451's
 * measurement behind it (16,784 keys / 20.4 MB before the items moved to the
 * chunked pages; it is EMPTY today). Excluding an empty namespace costs
 * nothing and stops a future refill from putting the boot back to 20 MB.
 */
const HYDRATION_EXCLUDED_PREFIXES: readonly string[] = [
  'freshcards:db:',
  'freshcards:hq_item:',
];

import { graphFetch, graphAuthProblem } from './graphAuth';
// F 452 — the app's bus, so a failed read can tell the operator that what
// they are looking at is cached.
import { eventBus } from '../event-bus';

export interface RemoteStateOptions {
  /** Defaults to same-origin `/api/fcc`. */
  endpoint?: string;
  /** Disable entirely — used by tests and by non-FCC deploys. */
  enabled?: boolean;
}

interface Envelope {
  ok: boolean;
  keys: string[];
  values: Record<string, string | null>;
  error?: string;
}

/** In-memory mirror. Source of truth for sync reads. */
const cache = new Map<string, string>();

/**
 * F 499 — THE CLIENT'S PER-KEY VERSION.
 *
 * WHAT THIS IS FOR. F 496 measured the loss: a deploy shipped a build with
 * no graph token, so the app could not read the server, it ran on local
 * storage alone, and its next write was PUSHED UP with the WHOLE key's
 * value — a full REPLACE. The browser's EMPTY copy overwrote the server's
 * 641-item database. The live receipt still read good.
 *
 * ⇒⇒⇒ THE VERSION IS WHAT MAKES THAT WRITE REFUSABLE. The client says
 * "I am writing version N+1 of this key". If the server is already at N+1 or
 * higher, the client is STALE — it is writing a copy it never read — and the
 * server answers 409 instead of losing the newer data.
 *
 * WHY THE VERSION IS TRACKED HERE AND NOT IN THE VALUE. The dispatch says
 * "every record in `freshcards:databases` and `freshcards:db:*` gets a
 * version field" and also "do not change the schema of existing records —
 * only add version". MEASURED, `freshcards:db:<id>:items:pNNNN` is an ARRAY
 * of items (394 elements in p0000, measured on production 2026-10-28) — an
 * array has no room for a field without changing its schema. So the version
 * travels BESIDE the value, in the same request body, and the record
 * payloads are byte-identical to what they were.
 *
 * WHERE THE NUMBER COMES FROM. The server's own version, read at hydration.
 * A key the server has no version for is version 0, so the first write of a
 * key that was never versioned is version 1.
 */
const keyVersions = new Map<string, number>();
/** The server's own version key for a graph key. Mirrors the server's. */
const VERSION_PREFIX = 'freshcards:__version__:';
/** The keys this guard governs. Mirrors the server's. */
function versionedKey(key: string): boolean {
  return key === 'freshcards:databases' || key.startsWith('freshcards:db:');
}

let hydrated = false;
let serverReachable = false;

/**
 * F 470 — THE HYDRATION HAS FINISHED (not merely started).
 *
 * `hydrated` is set at the TOP of `init()` and is a re-entrancy guard: it
 * says "an init is in flight", NOT "the data has arrived". The F 463
 * measurement is the proof: the 20.9 MB read landed at +4866 ms, and a
 * sidebar that waited on `hydrated` would have read an empty store.
 *
 * So this flag is flipped at the END of `init()`, on every path, and
 * `whenHydrated()` resolves then. A caller that waits on it waits for the
 * DATA.
 */
let hydrationComplete = false;
const hydrationWaiters = new Set<() => void>();

function markHydrationComplete(): void {
  if (hydrationComplete) return;
  hydrationComplete = true;
  for (const w of Array.from(hydrationWaiters)) w();
  hydrationWaiters.clear();
}

/** F 470 — TRUE when the hydration has finished. */
export function isHydrationComplete(): boolean {
  return hydrationComplete;
}

/**
 * F 470 — resolves when the hydration has finished.
 *
 * Resolves immediately if it already has. If the store was never
 * initialised there is nothing to wait for, and `init()` says so.
 */
export function whenHydrated(): Promise<void> {
  if (hydrationComplete) return Promise.resolve();
  return new Promise<void>((resolve) => { hydrationWaiters.add(resolve); });
}
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pendingKeys = new Set<string>();

const opts: Required<RemoteStateOptions> = {
  endpoint: '/api/fcc',
  enabled: true,
};

function isDeviceOnly(key: string): boolean {
  if (DEVICE_ONLY.has(key)) return true;
  if (key.startsWith('freshcards:listDensity:')) return true;
  if (key.startsWith('freshcards:galleryDensity:')) return true;
  return false;
}

function isSyncable(key: string): boolean {
  if (isDeviceOnly(key)) return false;
  return key.startsWith(SYNC_PREFIX) || LEGACY_SYNC_KEYS.includes(key);
}

/**
 * F 451 — may this key be MIRRORED into localStorage?
 *
 * Deliberately SEPARATE from `isSyncable`. `isSyncable` answers "is this
 * workspace data that belongs in the cache"; this answers "is it small enough
 * to persist to a ~5 MB browser store". Collapsing the two would mean an
 * oversized key stops syncing to the server as well, which is a bigger and
 * separate decision than "do not mirror 20 MB".
 *
 * Callers of `get()` are unaffected: the in-memory `cache` still holds the
 * value for the session, and the next `init()` re-reads it from the server.
 * What changes is only the browser's durable copy.
 */
function isMirrorable(key: string): boolean {
  for (const p of MIRROR_EXCLUDED_PREFIXES) {
    if (key.startsWith(p)) return false;
  }
  /**
   * F 486 — THE ITEMS MOVED, AND THE EXCLUSION DID NOT MOVE WITH THEM.
   *
   * F 470 migrated HQ Backup's 16,783 items OUT of `freshcards:hq_item:` and
   * INTO `freshcards:db:<id>:items:` — the layout F 470's own spec pins. The
   * exclusion above names the OLD namespace, so after the migration the mirror
   * copied every item page again.
   *
   * MEASURED on the live app, 2026-10-25 (a fresh browser profile):
   *   localStorage: 365 keys, 5,216,355 bytes — the ~5 MB quota, full.
   *   the six largest keys were `freshcards:db:page-1792674323869-h1kzdh6:items:pNNNN`,
   *   ~400,000 bytes each.
   * The origin was then full, so the app's own user record could not be saved
   * (`QuotaExceededError` on `freshcards:user:dev-freshcards-local`), the
   * unlock failed, and THE APP RENDERED NOTHING AT ALL.
   *
   * The same reasoning as F 451 applies to the new namespace: nothing reads
   * the item pages through `RemoteState` — the HQ reader goes straight to
   * `graphFetch` — so excluding them costs that reader nothing and keeps
   * 17.8 MB out of a 5 MB budget.
   */
  if (key.startsWith('freshcards:db:') && key.includes(':items:')) return false;
  return true;
}

/**
 * F 451 — ONE BAD KEY MUST NOT POISON THE WHOLE APP.
 *
 * `localStorage.setItem` throws `QuotaExceededError` once the origin's quota
 * is full, and it throws on the KEY THAT CROSSES THE LINE — which for a 20 MB
 * mirror is whichever of the 16,784 hq_item keys happens to be current. The
 * unguarded call meant that single key aborted the hydration loop, so every
 * key after it in the envelope was silently dropped AND the caller saw an
 * exception instead of a degraded store.
 *
 * The contract here is therefore: a failure to mirror one key is logged and
 * skipped, hydration continues, and the caller is never handed an exception
 * from a cache-write. Losing the browser's durable copy of some keys is
 * strictly better than losing the graph for the whole session.
 */
function mirrorToLocal(key: string, value: string): boolean {
  if (!isMirrorable(key)) return false;
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    // QuotaExceededError is the expected case; a SecurityError (storage
    // disabled, private mode) is handled identically, because the correct
    // behaviour is the same: keep the value in memory, do not give up.
    console.warn('[fcc] local mirror skipped for', key, (e as Error)?.name);
    return false;
  }
}

/**
 * F 390 — THE GRAPH IS AUTHENTICATED, so every request carries the token.
 *
 * The one place that knows this is `graphFetch`. `RemoteState` used to build
 * its own headers and sent no `Authorization` at all, which was correct when
 * the server asked for nothing and would have meant a 401 on every call now
 * that it does.
 */
async function request(path: string, init?: RequestInit): Promise<Response | null> {
  if (!opts.enabled) return null;
  return graphFetch(opts.endpoint, path, init);
}

/** Every localStorage key this module considers data. */
function localDataKeys(): string[] {
  const out: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && isSyncable(k)) out.push(k);
  }
  return out;
}

/**
 * Hydrate from the server, migrating local data up on first run.
 *
 * MIGRATION, one-time and non-destructive: if the server has no record of
 * any of these keys and the browser does, the browser's copy is pushed up.
 * If the server already has data, the server WINS — a device that is merely
 * a fresh browser must not overwrite another device's newer state.
 */
export async function init(options: RemoteStateOptions = {}): Promise<void> {
  Object.assign(opts, options);
  if (!opts.enabled) {
    // F 470 — There is no store to fill. Say the hydration is complete so a
    // caller waiting on it is not left hanging on a promise that will never
    // resolve.
    markHydrationComplete();
    return;
  }
  if (hydrated) return;
  hydrated = true;

  try {
  // 1. seed the cache from localStorage so a failed network still behaves
  //    exactly as the pre-F 144 app did.
  for (const k of localDataKeys()) cache.set(k, localStorage.getItem(k)!);

  // F 452 — the boot asks for everything EXCEPT the namespaces that are too
  // big to fetch on every start. The exclusion is applied by the service at
  // the store, so the excluded rows are never read — this is a smaller
  // download, not a smaller answer.
  const exclude = encodeURIComponent(HYDRATION_EXCLUDED_PREFIXES.join(','));
  const res = await request(`/kv?all=1&exclude=${exclude}`);
  if (!res || !res.ok) {
    serverReachable = false;
    // F 452 — the boot could not reach the graph. That is the condition the
    // offline banner is for, and the boot is the first moment it can be
    // known — so it is raised here, not only when a surface reads a page.
    eventBus.emit('graph:unreachable', { phase: 'hydration' });
    return; // localStorage remains the only store
  }
  serverReachable = true;
  eventBus.emit('graph:reachable', { phase: 'hydration' });

  const env = (await res.json()) as Envelope;
  const serverKeys = env.keys || [];
  const serverHasData = serverKeys.some((k) => isSyncable(k));

  if (!serverHasData) {
    // First run: this browser is the source of truth, so push it UP.
    //
    // The queue must be seeded explicitly. `init` filled the cache from
    // localStorage without going through `set()`, so nothing was pending and
    // the flush had nothing to send — migration silently did nothing and the
    // first browser to visit a fresh deployment would have "migrated" its
    // data into the void.
    for (const k of cache.keys()) if (isSyncable(k)) pendingKeys.add(k);
    await flush(true);
    return;
  }

  // server wins, and the superseded local copies are dropped
  //
  // F 451: `cache.set` is unconditional (in-memory is not bounded) but the
  // localStorage write goes through `mirrorToLocal`, which skips excluded
  // namespaces and survives a quota throw. A mirror failure no longer aborts
  // the loop, so one oversized key can no longer cost us the rest of the
  // hydration or push the app into permanent local-only mode.
  for (const [k, v] of Object.entries(env.values || {})) {
    if (v === null || v === undefined) continue;
    if (!isSyncable(k)) continue;
    cache.set(k, String(v));
    mirrorToLocal(k, String(v));

    // F 499 — READ THE SERVER'S VERSION FOR EACH GOVERNED KEY, so the first
    // write of this session sends the right number. A key the server has no
    // version for is version 0, so its first write is version 1.
    if (k.startsWith(VERSION_PREFIX)) {
      const target = k.slice(VERSION_PREFIX.length);
      const n = Number(String(v));
      if (Number.isInteger(n)) keyVersions.set(target, n);
    }
  }
  } finally {
    // F 470 — Every path out of `init()` lands here, so "the hydration
    // finished" is a fact and not a hope. A failed network finishes too:
    // the store is as hydrated as it is going to get.
    markHydrationComplete();
  }
}

/** Every key currently known, remote or local. Union, not just one side. */
export function keys(): string[] {
  const out = new Set<string>(cache.keys());
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && isSyncable(k)) out.add(k);
  }
  return [...out];
}

/**
 * Drop the in-memory cache and re-seed it from localStorage.
 *
 * F 145 — the cache is authoritative for reads, so anything that clears
 * localStorage BEHIND ITS BACK leaves it serving stale data. That is not
 * reachable in production (a reload rebuilds the cache), but it matters for
 * logout / reset flows and for any test that pokes the backing store directly.
 *
 * Without this, `localStorage.clear()` in a test looked like it worked while
 * the module under test kept returning the previous test's values.
 */
export function resetCache(): void {
  cache.clear();
  pendingKeys = new Set();
  for (const k of localDataKeys()) cache.set(k, localStorage.getItem(k)!);
}

export function isHydrated(): boolean {
  return hydrated;
}

export function isServerReachable(): boolean {
  return serverReachable;
}

/**
 * F 390 — WHY THE GRAPH IS UNREACHABLE, or null.
 *
 * A build with no graph token would lose the graph and say nothing. It says
 * this instead.
 */
export function remoteAuthProblem(): string | null {
  return graphAuthProblem();
}

/** Synchronous read — the whole point of the cache. */
export function get(key: string): string | null {
  return cache.get(key) ?? localStorage.getItem(key);
}

export function set(key: string, value: string): void {
  // F 451 — ORDER MATTERS HERE.
  //
  // The localStorage write used to come first and was unguarded, so a quota
  // throw here propagated out of `set()` to whoever was saving a page — the
  // user's edit would be lost AND the exception would surface in an unrelated
  // component. Mirroring is now attempted first but is allowed to fail: the
  // in-memory cache and the server flush below are what actually carry the
  // value, and a durable-copy failure must never cost the user their write.
  mirrorToLocal(key, value);
  if (!isSyncable(key)) return;
  cache.set(key, value);
  queueFlush(key);
}

export function remove(key: string): void {
  localStorage.removeItem(key);
  cache.delete(key);
  if (!isSyncable(key)) return;
  void request(`/kv/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

function queueFlush(key: string): void {
  pendingKeys.add(key);
  if (flushTimer) return;
  // Debounced: a burst of keystrokes is one request, not fifty.
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, 400);
}

/** Push pending keys to the server. */
export async function flush(immediate = false): Promise<boolean> {
  if (!serverReachable || pendingKeys.size === 0) return serverReachable;
  const keys = [...pendingKeys];
  pendingKeys = new Set();
  const values: Record<string, string> = {};
  const versions: Record<string, number> = {};
  for (const k of keys) {
    const v = cache.get(k);
    if (v !== undefined) values[k] = v;

    // F 499 — THE VERSION GOES OUT WITH THE VALUE.
    //
    // ⇒⇒⇒ THE CLIENT INCREMENTS ON EVERY WRITE: the version it sends is the
    // one it read at hydration, PLUS ONE. The server refuses a version OLDER
    // than the one it holds, so a client that is writing a copy it never
    // read — the F 496 failure — is refused with 409 instead of destroying
    // the newer data.
    //
    // ⇒⇒⇒ THE INCREMENT IS TAKEN WHEN THE WRITE IS QUEUED, NOT WHEN IT
    // LEAVES. Otherwise two flushes in the same tick would both send the
    // same number and the second would be refused as stale by the first's
    // own write.
    if (versionedKey(k)) {
      const current = keyVersions.get(k) ?? 0;
      const next = current + 1;
      versions[k] = next;
      keyVersions.set(k, next);
    }
  }
  if (immediate) pendingKeys.clear();
  const res = await request('/kv', {
    method: 'PUT',
    body: JSON.stringify({ values, versions }),
  });
  if (!res || !res.ok) {
    // put the keys back so a later flush retries rather than losing writes
    for (const k of keys) pendingKeys.add(k);
    // F 499 — A 409 MEANS THE SERVER IS NEWER THAN WE ARE. The version we
    // sent is already recorded as sent, so a retry would send the SAME
    // number and be refused again. ⇒⇒⇒ The client's version is rolled back
    // so the next write is judged against the server's state — which the
    // caller must refresh, hence the 409's own message.
    if (res && res.status === 409) {
      for (const k of keys) {
        if (versions[k] !== undefined) keyVersions.set(k, versions[k]! - 1);
      }
    }
    return false;
  }
  return true;
}

/** Flush before the tab goes away, so a close never eats the last edit. */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { void flush(true); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
}

/**
 * A StorageAdapter view over the same cache, for callers that already speak
 * the adapter interface.
 */
export const remoteAdapter: StorageAdapter = {
  async save(key, value) { set(key, JSON.stringify(value)); },
  async load(key) {
    const raw = get(key);
    if (raw === null) return null;
    try { return JSON.parse(raw); } catch { return null; }
  },
  async delete(key) { remove(key); },
  async list(prefix = '') {
    const keys = new Set<string>();
    for (const k of cache.keys()) if (k.startsWith(prefix)) keys.add(k);
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && isSyncable(k) && k.startsWith(prefix)) keys.add(k);
    }
    return [...keys];
  },
  async clear() {
    for (const k of [...cache.keys()]) if (isSyncable(k)) remove(k);
  },
  async export() {
    return new Blob([JSON.stringify(Object.fromEntries(cache))], { type: 'application/json' });
  },
  async import(data) {
    const text = await data.text();
    for (const [k, v] of Object.entries(JSON.parse(text))) set(k, String(v));
  },
} as StorageAdapter;

export const RemoteState = { init, get, set, remove, flush, keys, resetCache, isHydrated, isServerReachable, isHydrationComplete, whenHydrated, remoteAdapter };
export default RemoteState;
